const TMDB_API_KEY = import.meta.env.VITE_TMDB_API_KEY
const BASE_URL = 'https://api.themoviedb.org/3'
const IMG_BASE = 'https://image.tmdb.org/t/p'

export const posterUrl = (path, size = 'w342') =>
  path ? `${IMG_BASE}/${size}${path}` : null

export async function searchMovies(query) {
  if (!query || query.length < 2) return []
  const res = await fetch(
    `${BASE_URL}/search/movie?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}&include_adult=false`
  )
  const data = await res.json()
  const results = data.results || []

  // Sort by popularity, but only among results that actually contain the query.
  // TMDB already does relevance matching — we just re-sort by popularity so
  // blockbusters like "The Matrix" beat obscure films called "Matrix".
  results.sort((a, b) => (b.popularity || 0) - (a.popularity || 0))

  return results.slice(0, 8).map(formatMovie)
}

// Australian classification order for filtering comparisons
const AU_RATING_ORDER = ['G', 'PG', 'M', 'MA15+', 'R18+', 'X18+']

export function isRatingAllowed(movieRating, maxRating) {
  if (!maxRating || !movieRating) return true
  const movieIdx = AU_RATING_ORDER.indexOf(movieRating)
  const maxIdx = AU_RATING_ORDER.indexOf(maxRating)
  if (movieIdx === -1 || maxIdx === -1) return true // unknown ratings pass through
  return movieIdx <= maxIdx
}

// Map US ratings to AU equivalents for fallback
const US_TO_AU = {
  'G': 'G', 'PG': 'PG', 'PG-13': 'M', 'R': 'MA15+', 'NC-17': 'R18+', 'NR': null, 'Unrated': null,
}

export async function getMovieDetails(tmdbId) {
  const res = await fetch(`${BASE_URL}/movie/${tmdbId}?api_key=${TMDB_API_KEY}&append_to_response=credits,videos,watch/providers,release_dates`)
  const data = await res.json()
  const movie = formatMovie(data)

  // Content rating — AU first, US fallback
  const releaseDates = data.release_dates?.results || []
  const auRelease = releaseDates.find(r => r.iso_3166_1 === 'AU')
  const usRelease = releaseDates.find(r => r.iso_3166_1 === 'US')
  const auCert = auRelease?.release_dates?.find(d => d.certification)?.certification
  const usCert = usRelease?.release_dates?.find(d => d.certification)?.certification
  movie.content_rating = auCert || (usCert ? US_TO_AU[usCert] || usCert : null)
  // Attach top cast
  movie.cast = (data.credits?.cast || []).slice(0, 8).map(c => ({
    id: c.id,
    name: c.name,
    character: c.character,
    profile_url: c.profile_path ? `${IMG_BASE}/w185${c.profile_path}` : null,
  }))
  // Director
  const director = (data.credits?.crew || []).find(c => c.job === 'Director')
  movie.director = director ? { id: director.id, name: director.name } : null
  // Trailer — prefer official YouTube trailers, fall back to teasers
  const videos = data.videos?.results || []
  const trailer = videos.find(v => v.site === 'YouTube' && v.type === 'Trailer' && v.official)
    || videos.find(v => v.site === 'YouTube' && v.type === 'Trailer')
    || videos.find(v => v.site === 'YouTube' && v.type === 'Teaser')
  movie.trailer_key = trailer?.key || null
  // Watch providers — AU first, fall back to US
  const providers = data['watch/providers']?.results || {}
  const region = providers.AU || providers.US || null
  movie.watch_providers = {
    flatrate: (region?.flatrate || []).slice(0, 5).map(formatProvider),
    rent: (region?.rent || []).slice(0, 5).map(formatProvider),
    buy: (region?.buy || []).slice(0, 5).map(formatProvider),
    link: region?.link || null,
  }
  return movie
}

function formatProvider(p) {
  return {
    name: p.provider_name,
    logo_url: p.logo_path ? `${IMG_BASE}/w92${p.logo_path}` : null,
  }
}

export async function searchPeople(query) {
  if (!query || query.length < 2) return []
  const res = await fetch(
    `${BASE_URL}/search/person?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}&include_adult=false`
  )
  const data = await res.json()
  return (data.results || []).slice(0, 6).map(p => ({
    id: p.id,
    name: p.name,
    profile_url: p.profile_path ? `${IMG_BASE}/w185${p.profile_path}` : null,
    known_for: (p.known_for || []).map(k => k.title || k.name).filter(Boolean).slice(0, 3).join(', '),
    popularity: p.popularity,
  }))
}

export async function getMoviesForPerson(personId) {
  const res = await fetch(
    `${BASE_URL}/person/${personId}/movie_credits?api_key=${TMDB_API_KEY}`
  )
  const data = await res.json()
  // Merge cast + crew (directors show up in crew, not cast), dedupe by id
  const all = [...(data.cast || []), ...(data.crew || [])]
  const seen = new Set()
  return all
    .filter(m => {
      if (seen.has(m.id)) return false
      seen.add(m.id)
      return true
    })
    .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
    .slice(0, 30)
    .map(formatMovie)
}

// Search TMDB keywords (e.g. "rabbit", "heist", "christmas")
export async function searchKeywords(query) {
  if (!query || query.length < 2) return []
  const res = await fetch(
    `${BASE_URL}/search/keyword?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}`
  )
  const data = await res.json()
  return (data.results || []).slice(0, 8).map(k => ({
    id: k.id,
    name: k.name,
  }))
}

// Discover movies by keyword IDs — returns popular movies tagged with those keywords
export async function discoverByKeywords(keywordIds, page = 1) {
  if (!keywordIds || keywordIds.length === 0) return []
  const res = await fetch(
    `${BASE_URL}/discover/movie?api_key=${TMDB_API_KEY}&with_keywords=${keywordIds.join('|')}&sort_by=popularity.desc&include_adult=false&page=${page}`
  )
  const data = await res.json()
  return (data.results || []).slice(0, 20).map(formatMovie)
}

// Fetch backdrop images for a movie (used on Result page)
export async function getMovieBackdrops(tmdbId) {
  const res = await fetch(`${BASE_URL}/movie/${tmdbId}/images?api_key=${TMDB_API_KEY}`)
  const data = await res.json()
  return (data.backdrops || [])
    .filter(b => !b.iso_639_1 || b.iso_639_1 === 'en' || b.iso_639_1 === 'xx')
    .sort((a, b) => b.vote_average - a.vote_average)
    .slice(0, 6)
    .map(b => `${IMG_BASE}/w1280${b.file_path}`)
}

function formatMovie(m) {
  const genreArray = m.genres
    ? m.genres.map(g => g.name).filter(Boolean)
    : m.genre_ids
      ? m.genre_ids.map(id => GENRE_MAP[id] || '').filter(Boolean)
      : []

  return {
    tmdb_id: m.id,
    title: m.title,
    poster_url: m.poster_path ? posterUrl(m.poster_path) : null,
    backdrop_url: m.backdrop_path ? `${IMG_BASE}/w1280${m.backdrop_path}` : null,
    year: m.release_date ? parseInt(m.release_date.substring(0, 4)) : null,
    genre: genreArray.join(', '),  // legacy string field
    genres: genreArray,             // new array field for double-spin filter
    runtime_minutes: m.runtime || null,
    tmdb_rating: m.vote_average ? Math.round(m.vote_average * 10) / 10 : null,
    overview: m.overview || '',
  }
}

const GENRE_MAP = {
  28: 'Action', 12: 'Adventure', 16: 'Animation', 35: 'Comedy', 80: 'Crime',
  99: 'Documentary', 18: 'Drama', 10751: 'Family', 14: 'Fantasy', 36: 'History',
  27: 'Horror', 10402: 'Music', 9648: 'Mystery', 10749: 'Romance', 878: 'Science Fiction',
  10770: 'TV Movie', 53: 'Thriller', 10752: 'War', 37: 'Western',
}
